import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Paperclip, BellOff, Bell, ArrowLeft, Link2, X, Archive, Reply, Siren, Mic, Pencil, Check } from 'lucide-react';
import { VoiceRecorder } from './VoiceRecorder';
import { API_URL } from './chatShared';
import { playChatSound } from './chatSound';
import { ChatBubble } from './ChatBubble';
import { UseInContractDialog } from './UseInContractDialog';
import { AcksDialog } from './AcksDialog';
import { EmojiPicker } from './EmojiPicker';
import { EmojiText } from './EmojiText';
import { UrgencyToggle, UrgentBar, URGENCY } from './ChatUrgency';

const MANAGERS = ['super_admin', 'admin', 'gestor'];

const ReadOnlyBar = ({ conv, me, onOpenConv }) => {
  const openDirect = async (uid) => {
    try { const { data } = await axios.post(`${API_URL}/chat/conversations`, { type: 'direct', member_ids: [uid] }); onOpenConv?.(data.id); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao abrir conversa'); }
  };
  const join = async () => {
    try { await axios.post(`${API_URL}/chat/conversations/${conv.id}/join`); onOpenConv?.(conv.id); toast.success('Entrou no grupo'); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  return (
    <div className="p-3 border-t text-center space-y-2" data-testid="chat-readonly">
      <p className="text-xs text-slate-500">Está só a ler esta conversa (não faz parte dela).</p>
      <div className="flex flex-wrap justify-center gap-2">
        {conv.type === 'group'
          ? <button type="button" onClick={join} className="text-xs px-3 py-1.5 rounded-full bg-[#2D3E6F] text-white" data-testid="chat-join-btn">Entrar no grupo e responder</button>
          : conv.members.filter((m) => m.id !== me.id).map((m) => (
            <button key={m.id} type="button" onClick={() => openDirect(m.id)} className="text-xs px-3 py-1.5 rounded-full bg-[#2D3E6F] text-white" data-testid={`chat-reply-private-${m.id}`}>Responder a {m.name.split(' ')[0]}</button>
          ))}
      </div>
    </div>
  );
};

export const ChatThread = ({ conv, me, pendingRef, clearRef, onBack, onActivity, onOpenConv, compact, onClose }) => {
  const [msgs, setMsgs] = useState([]);
  const [archived, setArchived] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [useFile, setUseFile] = useState(null);
  const [acksMsg, setAcksMsg] = useState(null);
  const [urgency, setUrgency] = useState('');
  const [editing, setEditing] = useState(null);
  const [recording, setRecording] = useState(false);
  const startEdit = (m) => { setEditing(m); setReplyTo(null); setText(m.text || ''); setTimeout(() => inputRef.current?.focus(), 0); };
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const lastRef = useRef(null);
  const inputRef = useRef(null);
  const canWrite = conv.can_write ?? conv.is_member;

  const load = useCallback(async (initial) => {
    const params = !initial && lastRef.current ? { after: lastRef.current } : {};
    const { data } = await axios.get(`${API_URL}/chat/conversations/${conv.id}/messages`, { params });
    if (initial) { setMsgs(data.messages); setArchived(data.archived_hidden); } else if (data.messages.length) setMsgs((p) => [...p, ...data.messages.filter((m) => !p.some((x) => x.id === m.id))]);
    if (!initial && data.messages.some((m) => m.sender_id !== me.id)) playChatSound();
    if (data.messages.length) {
      lastRef.current = data.messages[data.messages.length - 1].created_at;
      axios.post(`${API_URL}/chat/conversations/${conv.id}/read`).then(onActivity).catch(() => {});
    }
  }, [conv.id, conv.is_member, onActivity, me.id]);

  useEffect(() => { lastRef.current = null; load(true).catch(() => {}); const t = setInterval(() => load(false).catch(() => {}), 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const refresh = () => load(true).catch(() => {});
    const t = setInterval(refresh, 10000);
    window.addEventListener('chat-ack', refresh);
    return () => { clearInterval(t); window.removeEventListener('chat-ack', refresh); };
  }, [load]);
  const names = Object.fromEntries(conv.members.map((x) => [x.id, x.name]));
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const reload = () => { lastRef.current = null; load(true).catch(() => {}); };
  const startReply = (m) => { setReplyTo(m); inputRef.current?.focus(); };
  const addEmoji = (e) => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? text.length;
    setText((t) => t.slice(0, pos) + e + t.slice(pos));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(pos + e.length, pos + e.length); }, 0);
  };
  const send = async () => {
    if (editing) {
      setSending(true);
      try { await axios.put(`${API_URL}/chat/messages/${editing.id}`, { text }); setText(''); setEditing(null); reload(); toast.success('Mensagem editada'); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao editar'); } finally { setSending(false); }
      return;
    }
    if (!text.trim() && !pendingRef) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/chat/conversations/${conv.id}/messages`, { text, ref: pendingRef || undefined, reply_to: replyTo?.id, urgency: urgency || undefined });
      setText(''); setReplyTo(null); setUrgency(''); clearRef?.(); await load(false); onActivity?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao enviar'); } finally { setSending(false); }
  };
  const sendFile = async (f, secs) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Máximo 10MB'); return; }
    const fd = new FormData(); fd.append('file', f); if (text.trim()) fd.append('text', text); if (replyTo) fd.append('reply_to', replyTo.id); if (urgency) fd.append('urgency', urgency); if (secs) fd.append('duration', String(secs));
    setSending(true);
    try { await axios.post(`${API_URL}/chat/conversations/${conv.id}/files`, fd); setText(''); setReplyTo(null); setUrgency(''); await load(false); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro no envio'); } finally { setSending(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const toggleMute = async () => {
    await axios.post(`${API_URL}/chat/conversations/${conv.id}/mute`, { muted: !conv.muted });
    toast.success(conv.muted ? 'Notificações ativadas' : 'Conversa silenciada'); onActivity?.();
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-thread">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
        <button type="button" onClick={onBack} className={`${compact ? '' : 'md:hidden'} p-1`} data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 truncate" data-testid="chat-thread-title">{conv.name}</p>
          <p className="text-xs text-slate-500 truncate">{conv.type === 'broadcast' ? `${conv.members.length} pessoas` : conv.members.map((m) => m.name).join(', ')}</p>
        </div>
        {conv.is_member && <button type="button" onClick={toggleMute} title={conv.muted ? 'Ativar notificações' : 'Silenciar'} className="p-2 text-slate-500 hover:text-slate-800" data-testid="chat-mute">{conv.muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}</button>}
        {onClose && <button type="button" onClick={onClose} title="Fechar" className="p-2 text-slate-500 hover:text-slate-800" data-testid="chat-thread-close"><X className="w-4 h-4" /></button>}
      </div>
      <UrgentBar msgs={msgs} />
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50">
        {archived && <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1"><Archive className="w-3 h-3" />Mensagens com mais de 12 meses estão arquivadas (acesso do Gestor).</p>}
        {msgs.map((m) => (
          <ChatBubble key={m.id} m={m} me={me} names={names} avatarUrl={conv.members.find((x) => x.id === m.sender_id)?.avatar_url} canSeeAcks={MANAGERS.includes(me.role)} onReply={canWrite ? startReply : null}
            onUseFile={setUseFile} onShowAcks={setAcksMsg} onChanged={reload} canResolve={conv.is_member} onEdit={canWrite ? startEdit : null} />
        ))}
        {!msgs.length && <p className="text-sm text-slate-400 text-center py-10">Ainda sem mensagens.</p>}
        <div ref={endRef} />
      </div>
      {canWrite ? (
        <div className="border-t border-slate-100 bg-white p-3 space-y-2">
          {pendingRef && (
            <div className="flex items-center gap-2 text-xs bg-sky-50 text-sky-800 rounded-lg px-2 py-1" data-testid="chat-pending-ref">
              <Link2 className="w-3 h-3" />{pendingRef.type === 'lead' ? 'Lead' : 'Contrato'}: {pendingRef.label}
              <button type="button" onClick={clearRef} className="ml-auto"><X className="w-3 h-3" /></button>
            </div>
          )}
          {replyTo && (
            <div className="flex items-start gap-2 text-xs bg-sky-100 border-l-4 border-sky-500 rounded-md px-2 py-1" data-testid="chat-reply-preview">
              <Reply className="w-3 h-3 mt-0.5 shrink-0 text-slate-500" />
              <span className="min-w-0 flex-1"><b>{replyTo.sender_name}</b><span className="block truncate text-slate-500">{replyTo.text ? <EmojiText text={replyTo.text} /> : replyTo.file?.name}</span></span>
              <button type="button" onClick={() => setReplyTo(null)} data-testid="chat-reply-cancel"><X className="w-3 h-3" /></button>
            </div>
          )}
          {editing && (
            <div className="flex items-start gap-2 text-xs bg-amber-50 border-l-4 border-amber-400 rounded-md px-2 py-1" data-testid="chat-edit-preview">
              <Pencil className="w-3 h-3 mt-0.5 shrink-0 text-amber-700" />
              <span className="min-w-0 flex-1"><b>A editar mensagem</b><span className="block truncate text-slate-500">{editing.text}</span></span>
              <button type="button" onClick={() => { setEditing(null); setText(''); }} data-testid="chat-edit-cancel"><X className="w-3 h-3" /></button>
            </div>
          )}
          {urgency && !editing && (
            <div className={`flex items-center gap-2 text-xs rounded-md border px-2 py-1 ${URGENCY[urgency].chip}`} data-testid="chat-urgency-preview">
              <Siren className="w-3.5 h-3.5 shrink-0" /><span className="flex-1"><b>{URGENCY[urgency].label}</b> — a próxima mensagem (texto ou ficheiro) fica em destaque até alguém carregar em “Resolvido”.</span>
              <button type="button" onClick={() => setUrgency('')} data-testid="chat-urgency-cancel"><X className="w-3 h-3" /></button>
            </div>
          )}
          {recording ? <VoiceRecorder onRecorded={(f, secs) => { setRecording(false); sendFile(f, secs); }} onCancel={() => setRecording(false)} /> : (
          <div className="flex items-end gap-1 sm:gap-2">
            {!editing && <button type="button" onClick={() => fileRef.current?.click()} className="p-2 text-slate-500 hover:text-slate-800" data-testid="chat-attach"><Paperclip className="w-5 h-5" /></button>}
            <EmojiPicker onPick={addEmoji} />
            {!editing && <UrgencyToggle value={urgency} onChange={setUrgency} />}
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx" onChange={(e) => sendFile(e.target.files[0])} data-testid="chat-file-input" />
            <textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} rows={1} maxLength={4000} placeholder="Mensagem…"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } if (e.key === 'Escape' && editing) { setEditing(null); setText(''); } }}
              className="flex-1 min-w-0 resize-none border border-slate-300 rounded-2xl px-3 py-2 text-sm max-h-32" data-testid="chat-input" />
            {!text.trim() && !pendingRef && !editing
              ? <button type="button" disabled={sending} onClick={() => setRecording(true)} title="Gravar nota de voz" className="p-2.5 rounded-full bg-[#2D3E6F] text-white hover:bg-[#24335c] disabled:opacity-50" data-testid="chat-voice-btn"><Mic className="w-4 h-4" /></button>
              : <button type="button" disabled={sending} onClick={send} className="p-2.5 rounded-full bg-[#2D3E6F] text-white hover:bg-[#24335c] disabled:opacity-50" data-testid="chat-send">{editing ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}</button>}
          </div>
          )}
        </div>
      ) : conv.type === 'broadcast' && conv.is_member
        ? <p className="p-3 text-xs text-center text-slate-500 border-t" data-testid="chat-readonly">Só a Gestão escreve nos Avisos.</p>
        : <ReadOnlyBar conv={conv} me={me} onOpenConv={onOpenConv} />}
      <UseInContractDialog file={useFile} onClose={() => setUseFile(null)} />
      <AcksDialog msg={acksMsg} onClose={() => setAcksMsg(null)} />
    </div>
  );
};
